import { useUserProfile } from "@/hooks/useUserProfile";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Building2 } from "lucide-react";

interface UserProfileMenuProps {
  collapsed?: boolean;
}

export function UserProfileMenu({ collapsed = false }: UserProfileMenuProps) {
  const { profile, tenant, primaryRole, currentPlan, isLoading } = useUserProfile();

  const getUserInitials = () => {
    if (!profile?.full_name) return "U";
    const names = profile.full_name.split(" ");
    return names.length > 1 
      ? `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase()
      : names[0][0].toUpperCase();
  };

  const getRoleBadge = () => {
    const badges = {
      super_admin: { label: 'Super Admin', variant: 'destructive' as const },
      admin: { label: 'Admin', variant: 'default' as const },
      employee: { label: 'Funcionário', variant: 'secondary' as const },
    };
    return badges[primaryRole as keyof typeof badges] || badges.employee;
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 p-2 rounded-lg animate-pulse">
        <div className="h-10 w-10 rounded-full bg-muted"></div>
        {!collapsed && <div className="h-4 w-24 bg-muted rounded"></div>}
      </div>
    );
  }

  const roleBadge = getRoleBadge();

  return (
    <div className="flex items-center gap-3 p-2 rounded-lg">
      <Avatar className="h-10 w-10 border-2 border-primary/20">
        <AvatarImage src={profile?.avatar_url || undefined} />
        <AvatarFallback className="bg-primary text-primary-foreground font-semibold">
          {getUserInitials()}
        </AvatarFallback>
      </Avatar>
      
      {!collapsed && (
        <div className="flex-1 text-left min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">
            {profile?.full_name || 'Usuário'}
          </p>
          
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            <Badge 
              variant={roleBadge.variant}
              className="text-xs px-1.5 py-0 h-4"
            >
              {roleBadge.label}
            </Badge>
            
            {currentPlan && (
              <Badge 
                variant="outline"
                className="text-xs px-1.5 py-0 h-4 border-primary/30"
              >
                {currentPlan.name}
              </Badge>
            )}
          </div>
          
          {tenant?.name && (
            <p className="text-xs text-muted-foreground truncate flex items-center gap-1 mt-0.5">
              <Building2 className="h-3 w-3 flex-shrink-0" />
              {tenant.name}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
